/**
 * Premium SaaS marketing components: animated counter, floating WhatsApp,
 * newsletter signup, bento grid, trust badges. Used alongside `blocks.jsx`.
 */
import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, ShieldCheck, Lock, Globe2, Sparkles, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { BRAND } from '../content';
import { toast } from 'sonner';

// ─── Animated counter ─────────────────────────────────
// Counts up from 0 to `to` when scrolled into view.
export function AnimatedCounter({ to = 0, prefix = '', suffix = '', duration = 1500, className = '' }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(eased * to));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration]);

  const formatted = value.toLocaleString('en-IN');
  return (
    <span ref={ref} className={className} data-testid="animated-counter">
      {prefix}{formatted}{suffix}
    </span>
  );
}

// ─── Floating WhatsApp button (global, sticky bottom-right) ─
export function FloatingWhatsApp({ message = 'Hi Founder! I came from the ActanonEdu website.' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Bubble preview */}
      {open && (
        <div
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-xs rounded-2xl bg-white dark:bg-mk-navy border border-mk-line dark:border-mk-edge shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-4"
          data-testid="float-wa-bubble"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-500 grid place-items-center text-white shrink-0">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm">ActanonEdu Founder</div>
              <div className="text-xs text-slate-500">Usually replies within 6 hours</div>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                Hi! Have a question about ActanonEdu? Tap below — I read every message myself.
              </p>
              <a
                href={BRAND.whatsappLink(message)}
                target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                data-testid="float-wa-bubble-cta"
              >
                <MessageCircle className="h-4 w-4" /> Start chat on WhatsApp
              </a>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {/* Floating action button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-2xl shadow-emerald-500/40 transition-all hover:scale-105 active:scale-95 grid place-items-center group"
        aria-label="Chat on WhatsApp"
        data-testid="float-wa-button"
      >
        <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-40 animate-ping" />
        <MessageCircle className="h-6 w-6 relative" />
      </button>
    </>
  );
}

// ─── Newsletter signup ─────────────────────────────────
export function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email.');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'home-page-newsletter' }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.detail || data.message || 'Subscribe failed');
      setDone(true);
      toast.success(data.alreadySubscribed
        ? 'You are already subscribed — see you on Sunday!'
        : 'Subscribed! Check your inbox for a welcome email from info@actanonedu.com');
      setEmail('');
    } catch (err) {
      toast.error(err?.message || 'Could not subscribe right now. Please try again.');
    }
  };

  return (
    <div className="rounded-3xl bg-mk-ice dark:bg-mk-navy border border-mk-line dark:border-mk-edge p-8 sm:p-12" data-testid="newsletter-signup">
      <div className="grid lg:grid-cols-2 gap-6 items-center">
        <div>
          <Sparkles className="h-6 w-6 mb-3 text-mk-blue" aria-hidden="true" />
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-mk-ink dark:text-white">Sunday teaching notes — in your inbox.</h3>
          <p className="mt-3 text-mk-muted dark:text-slate-300 text-sm sm:text-base max-w-md">
            One short, practical email every Sunday. Tactics, frameworks, and the rare AI tool worth your time. No spam, ever.
          </p>
        </div>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5" data-testid="newsletter-form">
          <Input
            type="email"
            placeholder="you@school.edu"
            aria-label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white text-mk-ink h-12 px-5 text-base placeholder:text-slate-400 border-mk-line rounded-full"
            data-testid="newsletter-email"
          />
          <Button type="submit" disabled={done} className="bg-mk-blue hover:bg-mk-deep text-white h-12 px-6 rounded-full font-semibold whitespace-nowrap" data-testid="newsletter-submit">
            {done ? (<><Check className="h-4 w-4 mr-1.5" /> Subscribed</>) : 'Subscribe free'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ─── Bento grid layout ─────────────────────────────────
// Children should set their own `col-span-X row-span-X` via className.
// Rows are taller on mobile (220px) so long descriptions in single-column
// stacked mode don't overflow, then shrink to 180px once the grid spreads.
export function BentoGrid({ children, className = '' }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[220px] sm:auto-rows-[180px] gap-4 ${className}`} data-testid="bento-grid">
      {children}
    </div>
  );
}

export function BentoCard({ children, className = '', tone = 'default', testid }) {
  const tones = {
    default: 'bg-white dark:bg-mk-navy text-mk-ink dark:text-slate-100 border-mk-line dark:border-mk-edge',
    indigo:  'bg-gradient-to-br from-mk-blue to-mk-deep text-white border-transparent',
    emerald: 'bg-gradient-to-br from-emerald-500 to-teal-700 text-white border-transparent',
    amber:   'bg-gradient-to-br from-amber-400 to-orange-600 text-white border-transparent',
    rose:    'bg-gradient-to-br from-rose-500 to-pink-700 text-white border-transparent',
    violet:  'bg-gradient-to-br from-mk-blue to-mk-deep text-white border-transparent',
    cyan:    'bg-gradient-to-br from-cyan-500 to-sky-700 text-white border-transparent',
    dark:    'bg-mk-midnight text-white border-mk-edge',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 sm:p-6 transition-all hover:-translate-y-0.5 hover:shadow-xl overflow-hidden ${tones[tone] || tones.default} ${className}`}
    >
      {children}
    </div>
  );
}

// ─── Trust badges (privacy, security, India-hosted) ────
export function TrustBadges() {
  const badges = [
    { icon: ShieldCheck, label: 'Data hosted in India' },
    { icon: Lock,        label: 'AES-256 · TLS 1.2+' },
    { icon: Globe2,      label: '7 Indian languages' },
    { icon: Sparkles,    label: 'Made for Indian boards' },
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-testid="trust-badges">
      {badges.map((b) => (
        <div key={b.label} className="flex items-center gap-2 text-xs sm:text-sm text-mk-muted dark:text-slate-300">
          <b.icon className="h-4 w-4 text-mk-blue" aria-hidden="true" />
          <span className="font-medium">{b.label}</span>
        </div>
      ))}
    </div>
  );
}
